import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  mapCurrencyRateFieldErrors,
  mapCurrencyRateHistoryResponseToDto,
  mapCurrencyRateResponseToDto,
  mapCurrencyRatesResponseToDto,
  validateCurrencyRateCreatePayload,
  validateCurrencyRateOverridePayload,
  validateCurrencyRateResetPayload,
} from '../dto/currency-rates.dto.js';
import {
  DEFAULT_CURRENCY_RATE_FILTERS,
  buildCurrencyRateRowsModel,
  filterCurrencyRateRows,
} from '../model/currency-rates.model.js';

const HISTORY_LIMIT = 50;
const DEFAULT_RELOAD_STATUS = {
  status: 'never',
  startedAt: null,
  completedAt: null,
  loadedCount: 0,
  errorMessage: null,
};

function buildEditForm(row) {
  return {
    rate: String(row?.rateUser ?? row?.effectiveRate ?? row?.rateBank ?? '').trim(),
    reason: '',
  };
}

function buildCreateForm() {
  return {
    currencyBase: 'EUR',
    currency: '',
    rate: '',
    reason: '',
  };
}

function replaceRate(rows, nextRate) {
  if (!nextRate) {
    return rows;
  }

  const nextRows = buildCurrencyRateRowsModel({ rates: [nextRate] });
  const nextRow = nextRows[0];
  if (!nextRow) {
    return rows;
  }

  const replaced = rows.map((row) => (row.currencyBase === nextRow.currencyBase && row.currency === nextRow.currency ? nextRow : row));
  const exists = replaced.some((row) => row.currencyBase === nextRow.currencyBase && row.currency === nextRow.currency);

  return exists ? replaced : [...replaced, nextRow];
}

function buildCurrencyPairKey(row) {
  const currencyBase = String(row?.currencyBase || '').trim().toUpperCase();
  const currency = String(row?.currency || '').trim().toUpperCase();

  return currencyBase && currency ? `${currencyBase}/${currency}` : '';
}

export function useCurrencyRates() {
  const { token, hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_CURRENCY_RATE_FILTERS);
  const [reloadStatus, setReloadStatus] = useState(DEFAULT_RELOAD_STATUS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalError, setModalError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState(buildCreateForm);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const formRef = useRef(form);
  const isCreateOpenRef = useRef(false);
  const editTargetRef = useRef(editTarget);
  const resetTargetRef = useRef(resetTarget);
  const rateStateRequestIdRef = useRef(0);
  const savingRequestIdRef = useRef(0);
  const historyRequestIdRef = useRef(0);
  const historyTargetRef = useRef(null);

  const canManageCurrencyRates = Boolean(hasPermission?.('settings.manage'));

  const startRateStateRequest = useCallback(() => {
    rateStateRequestIdRef.current += 1;
    return rateStateRequestIdRef.current;
  }, []);

  const isLatestRateStateRequest = useCallback((requestId) => requestId === rateStateRequestIdRef.current, []);

  const startSavingRequest = useCallback(() => {
    savingRequestIdRef.current += 1;
    return savingRequestIdRef.current;
  }, []);

  const isLatestSavingRequest = useCallback((requestId) => requestId === savingRequestIdRef.current, []);

  const isLatestHistoryRequest = useCallback((requestId, pairKey) => (
    requestId === historyRequestIdRef.current &&
    pairKey === historyTargetRef.current
  ), []);

  const invalidateHistoryRequest = useCallback(() => {
    historyRequestIdRef.current += 1;
    historyTargetRef.current = null;
    setIsHistoryLoading(false);
  }, []);

  const applyListResponse = useCallback((response, requestId) => {
    if (!isLatestRateStateRequest(requestId)) {
      return false;
    }

    const dto = mapCurrencyRatesResponseToDto(response);
    setRows(buildCurrencyRateRowsModel(dto));
    setReloadStatus(dto.lastReload || DEFAULT_RELOAD_STATUS);
    setIsLoading(false);

    return true;
  }, [isLatestRateStateRequest]);

  const loadRates = useCallback(async () => {
    const requestId = startRateStateRequest();
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getCurrencyRates(token);
      return applyListResponse(response, requestId);
    } catch (requestError) {
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      setRows([]);
      setReloadStatus(DEFAULT_RELOAD_STATUS);
      setError(requestError?.message || 'Failed to load currency rates.');
      setIsLoading(false);
      return false;
    } finally {
      if (isLatestRateStateRequest(requestId)) {
        setIsLoading(false);
      }
    }
  }, [applyListResponse, isLatestRateStateRequest, startRateStateRequest, token]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const visibleRows = useMemo(() => filterCurrencyRateRows(rows, filters), [rows, filters]);

  const updateFilter = useCallback((name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_CURRENCY_RATE_FILTERS);
  }, []);

  const refresh = useCallback(() => {
    setSuccess('');
    return loadRates();
  }, [loadRates]);

  const reloadRates = useCallback(async () => {
    const requestId = startRateStateRequest();
    const savingRequestId = startSavingRequest();
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.reloadCurrencyRates(token);
      if (!applyListResponse(response, requestId)) {
        return false;
      }

      setSuccess('Currency rates reloaded.');
      return true;
    } catch (requestError) {
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      setError(requestError?.message || 'Failed to reload currency rates.');
      setIsLoading(false);
      return false;
    } finally {
      if (isLatestSavingRequest(savingRequestId)) {
        setIsSaving(false);
      }
    }
  }, [applyListResponse, isLatestRateStateRequest, isLatestSavingRequest, startRateStateRequest, startSavingRequest, token]);

  const openCreate = useCallback(() => {
    const nextForm = buildCreateForm();
    invalidateHistoryRequest();
    isCreateOpenRef.current = true;
    editTargetRef.current = null;
    resetTargetRef.current = null;
    formRef.current = nextForm;
    setIsCreateOpen(true);
    setEditTarget(null);
    setResetTarget(null);
    setHistoryTarget(null);
    setHistoryRows([]);
    setHistoryTotal(0);
    setForm(nextForm);
    setFormErrors({});
    setModalError('');
    setSuccess('');
  }, [invalidateHistoryRequest]);

  const openEdit = useCallback((row) => {
    const nextForm = buildEditForm(row);
    invalidateHistoryRequest();
    isCreateOpenRef.current = false;
    editTargetRef.current = row;
    resetTargetRef.current = null;
    formRef.current = nextForm;
    setIsCreateOpen(false);
    setEditTarget(row);
    setResetTarget(null);
    setHistoryTarget(null);
    setHistoryRows([]);
    setHistoryTotal(0);
    setForm(nextForm);
    setFormErrors({});
    setModalError('');
    setSuccess('');
  }, [invalidateHistoryRequest]);

  const openReset = useCallback((row) => {
    const nextForm = { rate: '', reason: '' };
    invalidateHistoryRequest();
    isCreateOpenRef.current = false;
    resetTargetRef.current = row;
    editTargetRef.current = null;
    formRef.current = nextForm;
    setIsCreateOpen(false);
    setResetTarget(row);
    setEditTarget(null);
    setHistoryTarget(null);
    setHistoryRows([]);
    setHistoryTotal(0);
    setForm(nextForm);
    setFormErrors({});
    setModalError('');
    setSuccess('');
  }, [invalidateHistoryRequest]);

  const openHistory = useCallback(async (row) => {
    if (!row?.currencyBase || !row?.currency) {
      return false;
    }

    const requestId = historyRequestIdRef.current + 1;
    const pairKey = buildCurrencyPairKey(row);
    historyRequestIdRef.current = requestId;
    historyTargetRef.current = pairKey;
    isCreateOpenRef.current = false;
    editTargetRef.current = null;
    resetTargetRef.current = null;
    setIsCreateOpen(false);
    setHistoryTarget(row);
    setEditTarget(null);
    setResetTarget(null);
    setHistoryRows([]);
    setHistoryTotal(0);
    setModalError('');
    setIsHistoryLoading(true);

    try {
      const response = await adminApi.getCurrencyRateHistory(token, row.currencyBase, row.currency, {
        limit: HISTORY_LIMIT,
        offset: 0,
      });
      if (!isLatestHistoryRequest(requestId, pairKey)) {
        return false;
      }

      const dto = mapCurrencyRateHistoryResponseToDto(response);
      setHistoryRows(dto.history);
      setHistoryTotal(dto.total);
      return true;
    } catch (requestError) {
      if (!isLatestHistoryRequest(requestId, pairKey)) {
        return false;
      }

      setModalError(requestError?.message || 'Failed to load currency rate history.');
      return false;
    } finally {
      if (isLatestHistoryRequest(requestId, pairKey)) {
        setIsHistoryLoading(false);
      }
    }
  }, [isLatestHistoryRequest, token]);

  const closeModals = useCallback(() => {
    invalidateHistoryRequest();
    isCreateOpenRef.current = false;
    editTargetRef.current = null;
    resetTargetRef.current = null;
    formRef.current = buildCreateForm();
    setIsCreateOpen(false);
    setEditTarget(null);
    setResetTarget(null);
    setHistoryTarget(null);
    setHistoryRows([]);
    setHistoryTotal(0);
    setForm(buildCreateForm());
    setFormErrors({});
    setModalError('');
  }, [invalidateHistoryRequest]);

  const onFormChange = useCallback((name, value) => {
    formRef.current = {
      ...formRef.current,
      [name]: value,
    };
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [name]: undefined,
    }));
    setModalError('');
  }, []);

  const createRate = useCallback(async () => {
    if (!canManageCurrencyRates) {
      setModalError('Currency rates management permission is required.');
      return false;
    }

    if (!isCreateOpenRef.current) {
      setModalError('Currency rate creation form is required.');
      return false;
    }

    const currentForm = formRef.current;
    const payload = {
      currencyBase: String(currentForm.currencyBase || '').trim().toUpperCase(),
      currency: String(currentForm.currency || '').trim().toUpperCase(),
      rate: String(currentForm.rate || '').trim(),
      reason: String(currentForm.reason || '').trim(),
    };
    const errors = validateCurrencyRateCreatePayload(payload);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    setIsSaving(true);
    setModalError('');
    setSuccess('');

    const requestId = startRateStateRequest();
    const savingRequestId = startSavingRequest();

    try {
      const response = await adminApi.createCurrencyRate(token, payload);
      const dto = mapCurrencyRateResponseToDto(response);
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      setRows((current) => replaceRate(current, dto.rate));
      setIsLoading(false);
      setSuccess('Currency rate created.');
      closeModals();
      return true;
    } catch (requestError) {
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      const fieldErrors = mapCurrencyRateFieldErrors(requestError);
      setFormErrors(fieldErrors);
      setModalError(requestError?.message || 'Failed to create currency rate.');
      setIsLoading(false);
      return false;
    } finally {
      if (isLatestSavingRequest(savingRequestId)) {
        setIsSaving(false);
      }
    }
  }, [canManageCurrencyRates, closeModals, isLatestRateStateRequest, isLatestSavingRequest, startRateStateRequest, startSavingRequest, token]);

  const saveOverride = useCallback(async () => {
    if (!canManageCurrencyRates) {
      setModalError('Currency rates management permission is required.');
      return false;
    }

    const target = editTargetRef.current;
    const currentForm = formRef.current;

    if (!target?.currencyBase || !target?.currency) {
      setModalError('Currency pair is required.');
      return false;
    }

    const payload = {
      rate: String(currentForm.rate || '').trim(),
      reason: String(currentForm.reason || '').trim(),
    };
    const errors = validateCurrencyRateOverridePayload(payload);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    setIsSaving(true);
    setModalError('');
    setSuccess('');

    const requestId = startRateStateRequest();
    const savingRequestId = startSavingRequest();

    try {
      const response = await adminApi.updateCurrencyRateOverride(token, target.currencyBase, target.currency, payload);
      const dto = mapCurrencyRateResponseToDto(response);
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      setRows((current) => replaceRate(current, dto.rate));
      setIsLoading(false);
      setSuccess('Currency rate override saved.');
      closeModals();
      return true;
    } catch (requestError) {
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      const fieldErrors = mapCurrencyRateFieldErrors(requestError);
      setFormErrors(fieldErrors);
      setModalError(requestError?.message || 'Failed to save currency rate override.');
      setIsLoading(false);
      return false;
    } finally {
      if (isLatestSavingRequest(savingRequestId)) {
        setIsSaving(false);
      }
    }
  }, [canManageCurrencyRates, closeModals, isLatestRateStateRequest, isLatestSavingRequest, startRateStateRequest, startSavingRequest, token]);

  const confirmReset = useCallback(async () => {
    if (!canManageCurrencyRates) {
      setModalError('Currency rates management permission is required.');
      return false;
    }

    const target = resetTargetRef.current;
    const currentForm = formRef.current;

    if (!target?.currencyBase || !target?.currency) {
      setModalError('Currency pair is required.');
      return false;
    }

    const payload = {
      reason: String(currentForm.reason || '').trim(),
    };
    const errors = validateCurrencyRateResetPayload(payload);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    setIsSaving(true);
    setModalError('');
    setSuccess('');

    const requestId = startRateStateRequest();
    const savingRequestId = startSavingRequest();

    try {
      const response = await adminApi.resetCurrencyRateOverride(token, target.currencyBase, target.currency, payload);
      const dto = mapCurrencyRateResponseToDto(response);
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      setRows((current) => replaceRate(current, dto.rate));
      setIsLoading(false);
      setSuccess('Currency rate override reset.');
      closeModals();
      return true;
    } catch (requestError) {
      if (!isLatestRateStateRequest(requestId)) {
        return false;
      }

      const fieldErrors = mapCurrencyRateFieldErrors(requestError);
      setFormErrors(fieldErrors);
      setModalError(requestError?.message || 'Failed to reset currency rate override.');
      setIsLoading(false);
      return false;
    } finally {
      if (isLatestSavingRequest(savingRequestId)) {
        setIsSaving(false);
      }
    }
  }, [canManageCurrencyRates, closeModals, isLatestRateStateRequest, isLatestSavingRequest, startRateStateRequest, startSavingRequest, token]);

  return {
    rows,
    visibleRows,
    filters,
    reloadStatus,
    isLoading,
    isSaving,
    isHistoryLoading,
    error,
    success,
    modalError,
    formErrors,
    form,
    isCreateOpen,
    editTarget,
    resetTarget,
    historyTarget,
    historyRows,
    historyTotal,
    canManageCurrencyRates,
    updateFilter,
    resetFilters,
    refresh,
    reloadRates,
    openCreate,
    openEdit,
    openReset,
    openHistory,
    closeModals,
    onFormChange,
    createRate,
    saveOverride,
    confirmReset,
  };
}
